import React, { useState } from 'react';
import { Button, Card } from '../UI';
import { Users, Plus, Trash2, ArrowLeft, Edit2, Check, X, Heart, UserPlus } from 'lucide-react';
import IngredientSearch from './IngredientSearch';

// Diet styles
const DIET_STYLES = [
  { id: 'omnivore', name: 'Omnivore' },
  { id: 'vegan', name: 'Vegan' },
  { id: 'vegetarian', name: 'Vegetarian' },
  { id: 'pescatarian', name: 'Pescatarian' },
  { id: 'keto', name: 'Keto' },
  { id: 'paleo', name: 'Paleo' },
  { id: 'halal', name: 'Halal' },
  { id: 'kosher', name: 'Kosher' },
  { id: 'glutenFree', name: 'Gluten-Free' },
  { id: 'dairyFree', name: 'Dairy-Free' },
  { id: 'lowCarb', name: 'Low-Carb' },
  { id: 'mediterranean', name: 'Mediterranean' },
];

// Common allergens
const COMMON_ALLERGENS = [
  'Peanuts', 'Tree Nuts', 'Milk', 'Eggs', 'Soy', 
  'Wheat', 'Fish', 'Shellfish', 'Sesame'
];

const FamilyProfilesSetup = ({ onComplete, onSkip, onBack }) => {
  const [profiles, setProfiles] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  const handleAddProfile = (profile) => {
    setProfiles([...profiles, profile]);
    setShowAddForm(false);
  };

  const handleUpdateProfile = (index, profile) => {
    const updated = [...profiles];
    updated[index] = profile;
    setProfiles(updated);
    setEditingIndex(null);
  };

  const handleRemoveProfile = (index) => {
    setProfiles(profiles.filter((_, i) => i !== index));
  };

  const handleComplete = () => {
    onComplete(profiles);
  };

  // Empty state when no profiles
  if (profiles.length === 0 && !showAddForm && editingIndex === null) {
    return (
      <div className="min-h-screen flex items-center justify-center p-lg">
        <div className="w-full max-w-2xl">
          {/* Empty State */}
          <div className="text-center">
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mx-auto mb-lg">
              <Heart size={64} className="text-primary" />
            </div>

            <h1 className="text-display font-rounded text-primary mb-md">Care Profiles</h1>
            <p className="text-body text-text-secondary mb-xxxl max-w-md mx-auto leading-relaxed">
              Track dietary needs for the people you care about. Create personalized profiles to ensure everyone's safety and preferences.
            </p>

            <Button 
              variant="primary" 
              icon={<Plus size={20} />} 
              onClick={() => setShowAddForm(true)}
              className="mb-lg shadow-lg"
            >
              Add First Profile
            </Button>

            <Button variant="outline" onClick={onSkip}>
              Skip for now
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Main view with profiles
  return (
    <div className="min-h-screen flex items-center justify-center p-lg">
      <div className="w-full max-w-6xl">
        {/* Header */}
        <div className="text-center mb-xl">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mx-auto mb-md">
            <Users size={32} className="text-primary" />
          </div>
          <h1 className="text-display font-rounded text-primary mb-sm">Care Profiles</h1>
          <p className="text-body text-text-secondary">Manage dietary preferences for your loved ones</p>
        </div>

        {/* Profile Grid */}
        {profiles.length > 0 && !showAddForm && editingIndex === null && (
          <div className="mb-xl">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-lg mb-lg">
              {profiles.map((profile, index) => (
                <ProfileCard
                  key={index}
                  profile={profile}
                  onEdit={() => setEditingIndex(index)}
                  onDelete={() => handleRemoveProfile(index)}
                />
              ))}
            </div>

            {/* Add Another Button */}
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full p-xl border-2 border-dashed border-text-tertiary/30 rounded-large hover:border-primary/50 hover:bg-primary/5 transition-all group"
            >
              <div className="flex flex-col items-center gap-md">
                <div className="w-12 h-12 rounded-full bg-primary/10 group-hover:bg-primary/20 flex items-center justify-center transition-colors">
                  <UserPlus size={24} className="text-primary" />
                </div>
                <span className="text-callout font-semibold text-primary">Add Another Profile</span>
              </div>
            </button>
          </div>
        )}

        {/* Add/Edit Form */}
        {(showAddForm || editingIndex !== null) && (
          <ProfileForm
            profile={editingIndex !== null ? profiles[editingIndex] : null}
            onSave={(profile) => {
              if (editingIndex !== null) {
                handleUpdateProfile(editingIndex, profile);
              } else {
                handleAddProfile(profile);
              }
            }}
            onCancel={() => {
              setShowAddForm(false);
              setEditingIndex(null);
            }}
          />
        )}

        {/* Actions - Only show when not in add/edit mode */}
        {!showAddForm && editingIndex === null && (
          <div className="flex gap-md justify-center mt-xl pt-lg border-t border-text-tertiary/20">
            <Button variant="outline" icon={<ArrowLeft size={20} />} onClick={onBack}>
              Back
            </Button>
            <Button variant="outline" onClick={onSkip}>
              Skip
            </Button>
            <Button 
              variant="primary" 
              onClick={handleComplete} 
              disabled={profiles.length === 0}
              className="min-w-[200px]"
            >
              Complete ({profiles.length} {profiles.length === 1 ? 'profile' : 'profiles'})
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// Profile Card Component (matches iOS card design)
const ProfileCard = ({ profile, onEdit, onDelete }) => {
  const hasDietaryInfo = profile.dietaryProfile.dietStyle || 
                         profile.dietaryProfile.allergies.length > 0 || 
                         profile.dietaryProfile.restrictions.length > 0;

  return (
    <Card className="group hover:shadow-lg transition-all">
      {/* Header */}
      <div className="flex items-start justify-between mb-lg">
        <div>
          <h3 className="text-title-3 text-text-primary mb-xs">{profile.name}</h3>
          <p className="text-caption text-text-secondary capitalize">{profile.relationship}</p>
        </div>
        <div className="flex gap-sm opacity-0 group-hover:opacity-100 transition-opacity">
          <button 
            onClick={onEdit}
            className="w-8 h-8 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center transition-colors"
          >
            <Edit2 size={14} className="text-primary" />
          </button>
          <button 
            onClick={onDelete}
            className="w-8 h-8 rounded-full bg-error/10 hover:bg-error/20 flex items-center justify-center transition-colors"
          >
            <Trash2 size={14} className="text-error" />
          </button>
        </div>
      </div>

      {/* Dietary Info */}
      {hasDietaryInfo ? (
        <div className="space-y-md">
          {profile.dietaryProfile.dietStyle && (
            <div className="flex items-center gap-sm">
              <div className="w-2 h-2 rounded-full bg-primary"></div>
              <span className="text-callout text-text-primary">
                {DIET_STYLES.find(d => d.id === profile.dietaryProfile.dietStyle)?.name}
              </span>
            </div>
          )}

          {profile.dietaryProfile.allergies.length > 0 && (
            <div>
              <div className="flex items-center gap-sm mb-sm">
                <div className="w-2 h-2 rounded-full bg-error"></div>
                <span className="text-caption text-text-secondary font-semibold">Allergies</span>
              </div>
              <div className="flex flex-wrap gap-xs pl-md">
                {profile.dietaryProfile.allergies.slice(0, 3).map((allergen, i) => (
                  <span key={i} className="px-sm py-xs bg-error/10 text-error text-caption rounded-small">
                    {allergen}
                  </span>
                ))}
                {profile.dietaryProfile.allergies.length > 3 && (
                  <span className="px-sm py-xs bg-error/10 text-error text-caption rounded-small">
                    +{profile.dietaryProfile.allergies.length - 3}
                  </span>
                )}
              </div>
            </div>
          )}

          {profile.dietaryProfile.restrictions.length > 0 && (
            <div>
              <div className="flex items-center gap-sm mb-sm">
                <div className="w-2 h-2 rounded-full bg-accent"></div>
                <span className="text-caption text-text-secondary font-semibold">Avoids</span>
              </div>
              <div className="flex flex-wrap gap-xs pl-md">
                {profile.dietaryProfile.restrictions.slice(0, 2).map((restriction, i) => (
                  <span key={i} className="px-sm py-xs bg-accent/10 text-accent text-caption rounded-small">
                    {restriction}
                  </span>
                ))}
                {profile.dietaryProfile.restrictions.length > 2 && (
                  <span className="px-sm py-xs bg-accent/10 text-accent text-caption rounded-small">
                    +{profile.dietaryProfile.restrictions.length - 2}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <p className="text-caption text-text-tertiary italic">No dietary preferences set</p>
      )}
    </Card>
  );
};

// Profile Form Component (cleaner, step-by-step feel)
const ProfileForm = ({ profile, onSave, onCancel }) => {
  const isEditing = profile !== null;
  
  const [name, setName] = useState(profile?.name || '');
  const [relationship, setRelationship] = useState(profile?.relationship || 'family');
  const [dietStyle, setDietStyle] = useState(profile?.dietaryProfile.dietStyle || null);
  const [allergies, setAllergies] = useState(new Set(profile?.dietaryProfile.allergies || []));
  const [restrictions, setRestrictions] = useState(profile?.dietaryProfile.restrictions || []);
  const [customAllergen, setCustomAllergen] = useState('');
  const [showCustomAllergen, setShowCustomAllergen] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      name: name.trim(),
      relationship,
      dietaryProfile: {
        dietStyle,
        allergies: Array.from(allergies),
        restrictions,
      },
    });
  };

  const toggleAllergen = (allergen) => {
    const newAllergies = new Set(allergies);
    if (newAllergies.has(allergen)) {
      newAllergies.delete(allergen);
    } else {
      newAllergies.add(allergen);
    }
    setAllergies(newAllergies);
  };

  const addCustomAllergen = () => {
    if (customAllergen.trim() && !allergies.has(customAllergen.trim())) {
      const newAllergies = new Set(allergies);
      newAllergies.add(customAllergen.trim());
      setAllergies(newAllergies);
      setCustomAllergen('');
      setShowCustomAllergen(false);
    }
  };

  const customAllergies = Array.from(allergies).filter(a => !COMMON_ALLERGENS.includes(a));

  return (
    <form onSubmit={handleSubmit}>
      <Card className="mb-lg">
        <h2 className="text-title-2 text-primary mb-xl">
          {isEditing ? 'Edit Profile' : 'Add New Profile'}
        </h2>

        {/* Basic Info */}
        <div className="grid md:grid-cols-2 gap-lg mb-xxxl">
          <div>
            <label className="text-callout font-semibold text-text-primary mb-md block">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Family member's name"
              className="w-full px-lg py-md bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 text-body transition-all"
              required
            />
          </div>

          <div>
            <label className="text-callout font-semibold text-text-primary mb-md block">Relationship</label>
            <select
              value={relationship}
              onChange={(e) => setRelationship(e.target.value)}
              className="w-full px-lg py-md bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 text-body transition-all"
            >
              <option value="family">Family</option>
              <option value="partner">Partner</option>
              <option value="child">Child</option>
              <option value="parent">Parent</option>
              <option value="friend">Friend</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        {/* Diet Style */}
        <div className="mb-xxxl">
          <label className="text-callout font-semibold text-text-primary mb-md block">Diet Style (Optional)</label>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-sm">
            {DIET_STYLES.map((diet) => (
              <button
                key={diet.id}
                type="button"
                onClick={() => setDietStyle(dietStyle === diet.id ? null : diet.id)}
                className={`p-md rounded-medium border-2 text-left transition-all ${
                  dietStyle === diet.id
                    ? 'border-primary bg-primary/10 shadow-sm'
                    : 'border-text-tertiary/20 hover:border-primary/30 bg-surface'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-callout font-medium text-text-primary">{diet.name}</span>
                  {dietStyle === diet.id && <Check size={16} className="text-primary" />}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Allergies */}
        <div className="mb-xxxl">
          <label className="text-callout font-semibold text-text-primary mb-md block">Allergies (Optional)</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-sm mb-md">
            {COMMON_ALLERGENS.map((allergen) => (
              <button
                key={allergen}
                type="button"
                onClick={() => toggleAllergen(allergen)}
                className={`px-md py-sm rounded-medium text-callout font-medium transition-all ${
                  allergies.has(allergen)
                    ? 'bg-error/20 text-error border-2 border-error/50 shadow-sm'
                    : 'bg-surface border-2 border-text-tertiary/20 text-text-primary hover:border-error/30'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span>{allergen}</span>
                  {allergies.has(allergen) && <Check size={14} />}
                </div>
              </button>
            ))}
          </div>

          {customAllergies.length > 0 && (
            <div className="flex flex-wrap gap-sm mb-md">
              {customAllergies.map((allergen) => (
                <span key={allergen} className="px-md py-sm bg-error/20 text-error rounded-medium text-callout flex items-center gap-sm border border-error/30">
                  {allergen}
                  <button type="button" onClick={() => toggleAllergen(allergen)} className="hover:opacity-70">
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
          )}

          {showCustomAllergen ? (
            <div className="flex gap-sm">
              <input
                type="text"
                value={customAllergen}
                onChange={(e) => setCustomAllergen(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomAllergen())}
                placeholder="Enter custom allergen"
                className="flex-1 px-md py-sm bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-callout"
                autoFocus
              />
              <Button type="button" variant="primary" size="small" onClick={addCustomAllergen}>Add</Button>
              <Button type="button" variant="outline" size="small" onClick={() => setShowCustomAllergen(false)}>
                <X size={14} />
              </Button>
            </div>
          ) : (
            <Button type="button" variant="outline" icon={<Plus size={14} />} onClick={() => setShowCustomAllergen(true)} size="small">
              Add Custom Allergen
            </Button>
          )}
        </div>

        {/* Restrictions */}
        <div>
          <label className="text-callout font-semibold text-text-primary mb-md block">Ingredient Restrictions (Optional)</label>
          <IngredientSearch
            selectedIngredients={restrictions}
            onAdd={(ingredient) => setRestrictions([...restrictions, ingredient])}
            onRemove={(ingredient) => setRestrictions(restrictions.filter(r => r !== ingredient))}
          />
        </div>

        {/* Actions */}
        <div className="flex gap-md mt-xxxl pt-lg border-t border-text-tertiary/20">
          <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
            Cancel
          </Button>
          <Button type="submit" variant="primary" className="flex-1">
            {isEditing ? 'Save Changes' : 'Add Profile'}
          </Button>
        </div>
      </Card>
    </form>
  );
};

export default FamilyProfilesSetup;
